import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Clock, Trash2, ArrowRight, LayoutTemplate, Users } from "lucide-react";
import api from "@/lib/api";
import { totalDuration } from "@/lib/session";
import { toast } from "sonner";

function SessionCard({ s, onDelete, renderAction }) {
  const navigate = useNavigate();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/planner/${s.id}`)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/planner/${s.id}`)}
      className="text-left bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all relative group cursor-pointer"
      data-testid={`session-card-${s.id}`}
    >
      <h3 className="font-display text-lg font-semibold pr-6">{s.title}</h3>
      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{s.subtitle}</p>
      <div className="flex items-center gap-3 mt-4 text-xs font-mono text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" /> {totalDuration(s.segments)} min
        </span>
        <span>{s.segments.length} segments</span>
      </div>
      {renderAction ? (
        renderAction(s)
      ) : (
        onDelete && (
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-3 right-3 h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(s.id);
            }}
            data-testid={`session-delete-button-${s.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )
      )}
      <ArrowRight className="h-4 w-4 absolute bottom-5 right-5 text-[var(--brand-gold-text)] dark:text-[var(--brand-gold)] opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export default function MySessions() {
  const [sessions, setSessions] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [shared, setShared] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/sessions").then(({ data }) => setSessions(data));
    api.get("/sessions", { params: { is_template: true } }).then(({ data }) => setTemplates(data));
    api.get("/sessions/shared-with-me").then(({ data }) => setShared(data));
  }, []);

  const handleDelete = async (id) => {
    await api.delete(`/sessions/${id}`);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    toast.success("Session deleted");
  };

  const handleUseTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/sessions/${id}/use-template`);
      toast.success("New session created from template");
      navigate(`/planner/${data.id}`);
    } catch {
      toast.error("Could not create session from template");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100">My Sessions</h1>
            <p className="text-sm text-muted-foreground mt-1">Every prayer session you have formulated</p>
          </div>
          <Button onClick={() => navigate("/planner/new")} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="create-new-session-button">
            <Plus className="h-4 w-4 mr-2" /> New Session
          </Button>
        </div>

        <Tabs defaultValue="sessions" data-testid="my-sessions-tabs">
          <TabsList className="mb-6">
            <TabsTrigger value="sessions" data-testid="tab-sessions">
              Sessions
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">
              <LayoutTemplate className="h-3.5 w-3.5 mr-1.5" /> Templates
            </TabsTrigger>
            <TabsTrigger value="shared" data-testid="tab-shared">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Shared with Me
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sessions">
            {sessions === null && <p className="text-muted-foreground font-display italic">Loading sessions...</p>}
            {sessions && sessions.length === 0 && (
              <div className="text-center py-20 border border-dashed border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg" data-testid="empty-sessions-state">
                <p className="font-display text-xl text-muted-foreground italic">No sessions yet. Create your first one.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="sessions-grid">
              {sessions?.map((s) => (
                <SessionCard key={s.id} s={s} onDelete={handleDelete} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="templates">
            {templates === null && <p className="text-muted-foreground font-display italic">Loading templates...</p>}
            {templates && templates.length === 0 && (
              <div className="text-center py-20 border border-dashed border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg" data-testid="empty-templates-state">
                <p className="font-display text-xl text-muted-foreground italic">No templates yet. Save a session as a template from the planner.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="templates-grid">
              {templates?.map((s) => (
                <SessionCard
                  key={s.id}
                  s={s}
                  renderAction={() => (
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-white dark:bg-[var(--brand-card-dark)]"
                      onClick={(e) => handleUseTemplate(s.id, e)}
                      data-testid={`use-template-button-${s.id}`}
                    >
                      Use template
                    </Button>
                  )}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="shared">
            {shared === null && <p className="text-muted-foreground font-display italic">Loading shared sessions...</p>}
            {shared && shared.length === 0 && (
              <div className="text-center py-20 border border-dashed border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg" data-testid="empty-shared-state">
                <p className="font-display text-xl text-muted-foreground italic">No sessions have been shared with you yet.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="shared-grid">
              {shared?.map((s) => (
                <SessionCard key={s.id} s={s} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
